import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { Info24px } from "@similarweb/icons";
import { LEGEND_POSITIONS, AreaGraph, LegendExtension } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tab, TabList, Tabs } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { ISwitcherConfig } from "app/components/React/WidgetContainer/types";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { GranularitySwitcherEnum } from "app/pages/industry-analysis/category-share/CategoryShareGraph/CategoryShareGraphTypes";
import { GRANULARITY_SWITCHER_OPTIONS } from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getPNG } from "app/UtilitiesAndConstants/UtilityFunctions/getPNG";
import { GenericEmptyState } from "app/components/React/empty-state/GenericEmptyState";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";

import { EDataType } from "../../advertisers/overview/components/types";
import { useAdvertiserActivityDataAndParams } from "../../common/hooks/useAdvertiserActivityDataAndParams";
import { useWebSourceSubtitleFilter } from "../../common/hooks/useWebSourceSubtitleFilter";
import {
    getCustomGranularitySwitcherOptions,
    granularityNameToValue,
    granularityValueToName,
} from "../../common/overview/utils/graphUtils";
import { getErrorTypeFromTableNoDataType, getNoDataObject, isDailyGranularityLocked } from "../../common/utils/utils";

import {
    displayModeOptions,
    formatLegendValue,
    getChartConfig,
    hasNoDataInSelectedMetric,
    hasNoPerformanceData,
    metricTabs,
    TabsContainer,
    useGraphData,
} from "./PerformanceOverTimeGraphHelpers";
import { DISPLAY_MODES } from "./types";
import type { ChannelKey, IPerformanceOverTimeGraphProps } from "./types";
import { usePerformanceOverTime } from "./usePerformanceOverTime";

const TRACKING_CATEGORY = "Over Time Graph";

const GraphContainer = styled.div`
    padding-right: 8px;
    padding-left: 4px;
`;

const LegendExtensionContent = () => (
    <Stack
        gap="4px"
        background={colorsPalettes.bluegrey[100]}
        padding="8px"
        margin="16px 0 0 0"
        borderRadius="6px"
    >
        <Flex
            gap="4px"
            alignItems="center"
        >
            <Info24px
                size={24}
                color={colorsPalettes.carbon[400]}
            />
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon[500]}
            >
                {translate("advertiser.activity.overview.performance.over.time.widget.legend.title")}
            </Text>
        </Flex>
        <Text
            fontVariant="caption"
            color={colorsPalettes.carbon[400]}
        >
            {translate("advertiser.activity.overview.performance.over.time.widget.legend.text")}
        </Text>
    </Stack>
);

export const PerformanceOverTimeGraph = ({
    defaultGranularity = GranularitySwitcherEnum.WEEKLY,
    hideGranularityFilter = false,
}: IPerformanceOverTimeGraphProps) => {
    const params = useAdvertiserActivityDataAndParams();
    const chartRef = useRef(null);

    const [selectedGranularity, setSelectedGranularity] = useState<GranularitySwitcherEnum>(defaultGranularity);
    const [selectedMetric, setSelectedMetric] = useState<EDataType>(EDataType.SPEND);
    const [selectedDisplayModeIndex, setSelectedDisplayModeIndex] = useState<number>(0);
    const [selectedLegends, setSelectedLegends] = useState<ChannelKey[]>(["display", "search"]);

    const subtitleFilters = useSubtitleFilters("date", "country");
    const webSourceSubtitleFilter = useWebSourceSubtitleFilter(EDeviceType.TOTAL);
    const combinedSubtitleFilters = [...subtitleFilters, webSourceSubtitleFilter];

    const { data, isLoading, isError } = usePerformanceOverTime();

    const selectedDisplayMode = displayModeOptions[selectedDisplayModeIndex]?.value || DISPLAY_MODES.values;

    const { graphData: rawGraphData, selectedBreakdownData } = useGraphData(
        data,
        selectedGranularity,
        selectedMetric,
        selectedDisplayMode,
    );

    const graphData = useMemo(() => {
        return rawGraphData.map((item) => {
            const isAverageNA = !item.total;
            return {
                ...item,
                visible: selectedLegends.includes(item.id as ChannelKey),
                ...(isAverageNA ? { isDisabled: true } : {}),
            };
        });
    }, [rawGraphData, selectedLegends]);

    const customGranularitySwitcherOptions = getCustomGranularitySwitcherOptions(
        params.webSource,
        true, // Advertiser Activity always has ad intel metrics
        isLoading,
        false,
        isDailyGranularityLocked(),
    );

    useEffect(() => {
        if (isLoading) {
            return;
        }
        const currentGranularity = customGranularitySwitcherOptions.find(
            (option) => option.value === GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value,
        );
        if (!currentGranularity || currentGranularity.disabled) {
            const availableOption = [...customGranularitySwitcherOptions].reverse().find((option) => !option.disabled);

            if (availableOption) {
                setSelectedGranularity(granularityNameToValue[availableOption.value as GranularityNames]);
            }
        }
    }, [customGranularitySwitcherOptions, isLoading, selectedGranularity]);

    const legendValueFilter = useCallback(
        (value: number) => formatLegendValue(value, selectedMetric, selectedDisplayMode),
        [selectedMetric, selectedDisplayMode],
    );

    const handleGranularitySwitchChange = (filteredIndex: number) => {
        const item = customGranularitySwitcherOptions[filteredIndex];
        if (!item) {
            return;
        }
        const value = granularityNameToValue[item.value as GranularityNames];
        setSelectedGranularity(value);
        allTrackers.dispatchTrackEvent("Time Frame Button", "switch", `${TRACKING_CATEGORY}/${item.value}`);
    };

    const handleMetricChange = useCallback((index: number) => {
        const metric = metricTabs[index].type;
        setSelectedMetric(metric);
        allTrackers.dispatchTrackEvent("Graph Filter", "click", `${TRACKING_CATEGORY}/Metric/${metric}`);
    }, []);

    const handleDisplayModeChange = useCallback((index: number) => {
        setSelectedDisplayModeIndex(index);
        const mode = displayModeOptions[index]?.value;
        if (mode) {
            allTrackers.dispatchTrackEvent("Display Mode", "switch", `${TRACKING_CATEGORY}/${mode}`);
        }
    }, []);

    const handleLegendToggle = useCallback((filteredData: any[]) => {
        const visibleLegendIds = filteredData.map((item) => item.id);
        setSelectedLegends(visibleLegendIds);
    }, []);

    const granularitySelectedIndex = customGranularitySwitcherOptions.findIndex(
        (option) => option.value === GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value,
    );

    const switcherGranularity: ISwitcherConfig = {
        items: customGranularitySwitcherOptions,
        onItemClick: handleGranularitySwitchChange,
        initialSelectedItem: granularitySelectedIndex,
    };

    const switcherDisplayMode: ISwitcherConfig = {
        items: displayModeOptions.map((item) => ({
            ...item,
            disabled: isLoading,
        })),
        onItemClick: handleDisplayModeChange,
        initialSelectedItem: selectedDisplayModeIndex,
    };

    const durationData = DurationService.getDurationData(params.duration);
    const chartConfig = useMemo(
        () =>
            getChartConfig(
                selectedMetric,
                selectedDisplayMode,
                selectedGranularity,
                selectedBreakdownData,
                durationData.raw.to,
            ),
        [selectedMetric, selectedDisplayMode, selectedGranularity, selectedBreakdownData, durationData.raw.to],
    );

    const hasNoData = hasNoPerformanceData(...Object.values(selectedBreakdownData));
    const hasNoDataForSelectedMetric = hasNoDataInSelectedMetric(rawGraphData);

    const handleDownloadClick = () => {
        if (!chartRef.current) {
            return;
        }
        return getPNG(
            chartRef,
            0,
            50,
            translate("advertiser.activity.overview.performance.over.time.widget.title"),
        )?.();
    };

    const noDataText = getNoDataObject(isError);

    return (
        <div ref={chartRef}>
            <WidgetContainer
                key={`${selectedMetric}-${selectedDisplayModeIndex}-${selectedGranularity}`}
                data-automation="advertiser-activity-performance-overtime-graph"
                withBorder
                title={translate("advertiser.activity.overview.performance.over.time.widget.title")}
                tooltip={translate("advertiser.activity.overview.performance.over.time.widget.tooltip")}
                subtitleFilters={combinedSubtitleFilters}
                showSubtitleFiltersLoadingState={false}
                isLoading={isLoading}
                loaderWidth={"100%"}
                loaderHeight={384}
                noData={!isLoading ? hasNoData : null}
                noDataTitle={noDataText.title}
                noDataSubtitle={noDataText.subtitle}
                noDataType={getErrorTypeFromTableNoDataType(noDataText.type)}
                dynamicSwitcherConfig={
                    hideGranularityFilter ? [switcherDisplayMode] : [switcherDisplayMode, switcherGranularity]
                }
                onDownloadClick={handleDownloadClick}
            >
                <TabsContainer>
                    <Tabs
                        selectedIndex={metricTabs.findIndex((tab) => tab.type === selectedMetric)}
                        onSelect={handleMetricChange}
                    >
                        <TabList>
                            {metricTabs.map((tab, index) => {
                                const isSelected = metricTabs.findIndex((t) => t.type === selectedMetric) === index;
                                const IconComponent = tab.Icon;
                                return (
                                    <Tab key={tab.type}>
                                        <Flex gap="8px">
                                            <IconComponent
                                                size="xs"
                                                color={isSelected ? colorsPalettes.blue[400] : undefined}
                                            />
                                            {translate(tab.labelKey)}
                                        </Flex>
                                    </Tab>
                                );
                            })}
                        </TabList>
                    </Tabs>
                </TabsContainer>
                {hasNoDataForSelectedMetric ? (
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        height="380px"
                    >
                        <GenericEmptyState
                            isFullPage={false}
                            title={noDataText.title}
                            subtitle={noDataText.subtitle}
                        />
                    </Flex>
                ) : (
                    <GraphContainer>
                        <AreaGraph
                            shouldPresentLegend
                            shouldPresentValueAtLegend
                            legendPosition={LEGEND_POSITIONS.RIGHT}
                            legendValueFilter={legendValueFilter}
                            data={graphData}
                            graphConfig={chartConfig}
                            legendGraphGap={24}
                            legendToggleHandlerCallback={handleLegendToggle}
                            isToggleAllEnabled={false}
                            legendHeaders={[
                                translate("advertiser.activity.overview.performance.over.time.widget.legend.channel"),
                                translate(
                                    `advertiser.activity.overview.performance.over.time.widget.legend.${granularityValueToName[selectedGranularity].toLowerCase()}.average`,
                                    "advertiser.activity.overview.performance.over.time.widget.legend.weekly.average",
                                ),
                            ]}
                            disabledLegendItemTooltip={translate(
                                "advertiser.activity.overview.performance.over.time.widget.legend.disabled.tooltip",
                            )}
                        >
                            <LegendExtension position="after">
                                <LegendExtensionContent />
                            </LegendExtension>
                        </AreaGraph>
                    </GraphContainer>
                )}
            </WidgetContainer>
        </div>
    );
};
